import React from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserDisplayName } from '../../utils/adminUtils';

const drawerWidth = 280;

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface MenuItemProps {
  text: string;
  icon: React.ReactElement;
  path: string;
  requiredRole?: 'ADMIN' | 'MANAGER' | 'USER';
  subItems?: MenuItemProps[];
}

const menuItems: MenuItemProps[] = [
  { text: 'Go to Store', icon: <HomeIcon />, path: '/' },
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin', requiredRole: 'ADMIN' },
  { text: 'User Management', icon: <PeopleIcon />, path: '/admin/users', requiredRole: 'ADMIN' },
  { text: 'Product Management', icon: <InventoryIcon />, path: '/admin/products', requiredRole: 'ADMIN' },
  { text: 'Order Management', icon: <ShoppingCartIcon />, path: '/admin/orders', requiredRole: 'ADMIN' },
  { text: 'Analytics', icon: <AssessmentIcon />, path: '/admin/analytics', requiredRole: 'ADMIN' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings', requiredRole: 'ADMIN' },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Get user role from localStorage
  const userRole = localStorage.getItem('userRole') as 'ADMIN' | 'MANAGER' | 'USER' | null;

  // Filter menu items based on user role
  const getFilteredMenuItems = () => {
    return menuItems.filter(item => {
      if (!item.requiredRole) return true;
      if (!userRole) return false;
      
      // Admin can access everything
      if (userRole === 'ADMIN') return true;
      
      // For other roles, check specific permissions
      return item.requiredRole === userRole;
    });
  };

  const filteredMenuItems = getFilteredMenuItems();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Clear all auth data and redirect
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isMenuOpen = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, color: '#333' }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, color: '#333', fontWeight: 600 }}
          >
            Admin Dashboard
          </Typography>
          
          <IconButton size="large" color="inherit">
            <NotificationsIcon sx={{ color: '#666' }} />
          </IconButton>
          
          <IconButton
            size="large"
            edge="end"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#35C5F0' }}>
              {getUserDisplayName().charAt(0)}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={isMenuOpen}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          navigate('/');
          handleProfileMenuClose();
        }}>
          <ListItemIcon>
            <StorefrontIcon fontSize="small" />
          </ListItemIcon>
          View Store
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#1a1a1a',
              color: '#fff',
              borderRight: 'none',
            },
          }}
        >
          {/* Drawer Content */}
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography
              variant="h5"
              component="div"
              onClick={() => {
                navigate('/');
                setMobileOpen(false);
              }}
              sx={{
                fontWeight: 700,
                color: '#35C5F0',
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  color: '#2ba3cc',
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              DEWFOAM
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#888',
                fontSize: '0.75rem',
              }}
            >
              Admin Panel
            </Typography>
          </Box>
          
          <Divider sx={{ borderColor: '#333' }} />
          
          <List sx={{ mt: 2 }}>
            {filteredMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }}
                  sx={{
                    mx: 2,
                    borderRadius: 2,
                    backgroundColor: location.pathname === item.path ? '#35C5F0' : 'transparent',
                    color: location.pathname === item.path ? '#fff' : '#ccc',
                    '&:hover': {
                      backgroundColor: location.pathname === item.path ? '#2ba3cc' : '#333',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: location.pathname === item.path ? '#fff' : '#888',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        fontSize: '0.9rem',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#1a1a1a',
              color: '#fff',
              borderRight: 'none',
            },
          }}
          open
        >
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography
              variant="h5"
              component="div"
              onClick={() => navigate('/')}
              sx={{
                fontWeight: 700,
                color: '#35C5F0',
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  color: '#2ba3cc',
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              DEWFOAM
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#888',
                fontSize: '0.75rem',
              }}
            >
              Admin Panel
            </Typography>
          </Box>
          
          <Divider sx={{ borderColor: '#333' }} />
          
          <List sx={{ mt: 2 }}>
            {filteredMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    mx: 2,
                    borderRadius: 2,
                    backgroundColor: location.pathname === item.path ? '#35C5F0' : 'transparent',
                    color: location.pathname === item.path ? '#fff' : '#ccc',
                    '&:hover': {
                      backgroundColor: location.pathname === item.path ? '#2ba3cc' : '#333',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: location.pathname === item.path ? '#fff' : '#888',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        fontSize: '0.9rem',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          pt: '64px',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;